import { useEffect, useMemo, useRef, useState } from 'react'
import type { DragEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSession } from '@/context/AuthContext'
import {
  Plus, Search, X, CalendarRange, LayoutGrid, CalendarDays, User, FolderKanban,
  ChevronRight, Layers,
} from 'lucide-react'
import type { ContentPlan } from '@/lib/types'
import { useContentPlans } from '@/lib/useContentPlans'
import { ContentPlanModal } from '@/components/ContentPlanModal'
import {
  CONTENT_STATUS_CONFIG, CONTENT_STATUS_ORDER, channelTint, formatPlanDate, dayNameLong,
} from '@/lib/contentPlan'
import { hasCrossDeptView } from '@/lib/roles'
import { deptColor, DEPARTMENTS } from '@/lib/departments'

// ── Toggle Content ↔ Kalender (penghubung dua tampilan) ─────
function ViewToggle({ active }: { active: 'content' | 'calendar' }) {
  const navigate = useNavigate()
  const Btn = ({ id, label, Icon, href }: { id: 'content' | 'calendar'; label: string; Icon: typeof LayoutGrid; href: string }) => (
    <button onClick={() => navigate(href)}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-body font-medium transition-all ${active === id ? 'bg-white text-[#111110] shadow-sm' : 'text-[#8A8A85] hover:text-[#1A1916]'}`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  )
  return (
    <div className="flex items-center gap-1 bg-[#F4F3F0] rounded-xl p-1 flex-shrink-0">
      <Btn id="content" label="Content" Icon={LayoutGrid} href="/content-plan" />
      <Btn id="calendar" label="Kalender" Icon={CalendarRange} href="/calendar-plan" />
    </div>
  )
}

// ── Funnel compact (channel → items) ────────────────────────
function FunnelView({ plan }: { plan: ContentPlan }) {
  if (!plan.funnel.length) return null
  return (
    <div className="space-y-1.5">
      {plan.funnel.map((c, i) => {
        const t = channelTint(c.channel)
        return (
          <div key={i} className="flex items-start gap-2">
            <span className="mt-0.5 inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-body font-bold uppercase tracking-wide border flex-shrink-0"
              style={{ backgroundColor: t.bg, borderColor: t.border, color: t.text }}>
              {c.channel}
            </span>
            <span className="text-[11px] text-[#6B6860] font-body leading-relaxed">{c.items.join(' · ') || '—'}</span>
          </div>
        )
      })}
    </div>
  )
}

function ContentCard({ plan, highlight, dragging, onOpen, onJump, onDragStart, onDragEnd }: {
  plan: ContentPlan
  highlight: boolean
  dragging: boolean
  onOpen: () => void
  onJump: () => void
  onDragStart: (e: DragEvent<HTMLDivElement>) => void
  onDragEnd: () => void
}) {
  const dc = deptColor(plan.department)
  return (
    <div
      id={`content-${plan.id}`}
      onClick={onOpen}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      title="Seret ke kolom lain untuk ubah status"
      className={`group relative bg-white border rounded-xl p-3.5 flex flex-col cursor-grab active:cursor-grabbing hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow,border-color,opacity] duration-200 ${dragging ? 'opacity-40' : ''} ${highlight ? 'border-[#6366F1] ring-2 ring-[#6366F1]/30' : 'border-[#E8E7E3]'}`}
      style={{ borderLeftWidth: '3px', borderLeftColor: plan.color }}
    >
      {/* Date + dept */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-body font-semibold text-[#6B6860]">
          <CalendarDays className="w-3 h-3 text-[#AEADA8]" />
          {dayNameLong(plan.planDate)}, {formatPlanDate(plan.planDate)}
        </span>
        {plan.department && (
          <span className="text-[8px] font-body font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md border flex-shrink-0"
            style={{ backgroundColor: dc.bg, color: dc.text, borderColor: dc.border }}>
            {plan.department}
          </span>
        )}
      </div>

      {/* Title */}
      <h3 className="font-display text-[14px] font-bold text-[#1A1916] leading-snug mb-2.5 group-hover:text-[#6366F1] transition-colors">
        {plan.title}
      </h3>

      {/* Content Bank */}
      {plan.contentBank.length > 0 && (
        <div className="mb-2.5">
          <p className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-[#C0BDB6] font-body font-semibold mb-1.5">
            <Layers className="w-2.5 h-2.5" /> Content Bank
          </p>
          <div className="flex flex-wrap gap-1">
            {plan.contentBank.map((b, i) => (
              <span key={i} className="px-1.5 py-0.5 rounded-md bg-[#F4F3F0] text-[10px] font-body text-[#3A3A36]">{b}</span>
            ))}
          </div>
        </div>
      )}

      {/* Funnel */}
      {plan.funnel.length > 0 && (
        <div className="mb-2.5">
          <p className="text-[9px] uppercase tracking-wider text-[#C0BDB6] font-body font-semibold mb-1.5">Funnel</p>
          <FunnelView plan={plan} />
        </div>
      )}

      {/* Footer */}
      <div className="mt-auto pt-2.5 border-t border-[#F4F3F0] flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          {plan.pic.length > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] text-[#8A8A85] font-body truncate">
              <User className="w-3 h-3 text-[#C0BDB6]" /> {plan.pic.join(', ')}
            </span>
          )}
          {plan.projectName && (
            <span className="inline-flex items-center gap-1 text-[10px] text-[#6366F1] font-body truncate" title={`Project: ${plan.projectName}`}>
              <FolderKanban className="w-3 h-3" /> {plan.projectName}
            </span>
          )}
        </div>
        <button onClick={(e) => { e.stopPropagation(); onJump() }}
          title="Lihat di kalender"
          className="flex items-center gap-0.5 text-[10px] font-body text-[#AEADA8] hover:text-[#6366F1] transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100">
          Kalender <ChevronRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  )
}

export default function ContentPlanPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { data: session } = useSession()
  const { plans, loading, createPlan, updatePlan, deletePlan } = useContentPlans()

  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<ContentPlan | null>(null)
  const [creating, setCreating] = useState<{ date?: string } | null>(null)
  const [highlightId, setHighlightId] = useState<string | null>(null)
  const [selectedDept, setSelectedDept] = useState<string | null>(null)
  const holding = hasCrossDeptView(session?.user ?? {})

  // Tangani deep-link dari kalender: ?focus=<id> → sorot & buka kartunya.
  const handledFocus = useRef(false)
  useEffect(() => {
    if (handledFocus.current || loading) return
    const focus = params.get('focus')
    const date = params.get('date')
    if (focus) {
      handledFocus.current = true
      const target = plans.find((p) => p.id === focus)
      setHighlightId(focus)
      setTimeout(() => {
        document.getElementById(`content-${focus}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 120)
      setTimeout(() => setHighlightId(null), 2600)
      if (target) setEditing(target)
      navigate('/content-plan', { replace: true })
    } else if (date) {
      handledFocus.current = true
      setCreating({ date })
      navigate('/content-plan', { replace: true })
    }
  }, [params, plans, loading, navigate])

  const deptOptions = useMemo(
    () => Array.from(new Set([...DEPARTMENTS, ...plans.map((p) => p.department).filter(Boolean) as string[]])),
    [plans],
  )

  const filtered = useMemo(() => {
    let list = plans
    if (holding && selectedDept) list = list.filter((p) => p.department === selectedDept)
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter((p) =>
        p.title.toLowerCase().includes(q) ||
        p.notes.toLowerCase().includes(q) ||
        p.contentBank.some((b) => b.toLowerCase().includes(q)) ||
        p.funnel.some((f) => f.channel.toLowerCase().includes(q) || f.items.some((it) => it.toLowerCase().includes(q))) ||
        p.pic.some((name) => name.toLowerCase().includes(q)),
      )
    }
    return list
  }, [plans, search, holding, selectedDept])

  const stats = useMemo(() => ({
    total: filtered.length,
    draft: filtered.filter((p) => p.status === 'draft').length,
    planned: filtered.filter((p) => p.status === 'planned').length,
    inprogress: filtered.filter((p) => p.status === 'in-progress').length,
    published: filtered.filter((p) => p.status === 'published').length,
  }), [filtered])

  const goCalendar = (plan: ContentPlan) => navigate(`/calendar-plan?focus=${plan.id}`)

  // Drag & drop antar kolom status (HTML5 native — tanpa library).
  const [dragId, setDragId] = useState<string | null>(null)
  const [overCol, setOverCol] = useState<ContentPlan['status'] | null>(null)
  const dropTo = (status: ContentPlan['status']) => {
    const id = dragId
    setDragId(null); setOverCol(null)
    const plan = id ? plans.find((p) => p.id === id) : null
    if (!plan || plan.status === status) return
    updatePlan(plan.id, { status })
  }

  return (
    <div className="min-h-screen bg-[#F5F4F0]">
      {/* Header */}
      <header className="bg-white border-b border-[#E8E7E3] sticky top-0 z-30">
        <div className="px-6 flex items-center gap-4" style={{ height: '52px' }}>
          <span className="font-display text-[15px] font-bold text-[#1A1916] flex-shrink-0">Content Plan</span>
          <ViewToggle active="content" />
          <div className="flex-1 max-w-sm mx-auto relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#C0BDB6] pointer-events-none" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari konten, channel, PIC..."
              className="w-full pl-9 pr-8 py-1.5 bg-[#F4F3F0] border border-transparent rounded-xl text-sm font-body text-[#1A1916] placeholder:text-[#C0BDB6] focus:outline-none focus:bg-white focus:border-[#E0DFD8] transition-all" />
            {search && <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2"><X className="w-3.5 h-3.5 text-[#AEADA8]" /></button>}
          </div>
          {holding && (
            <select value={selectedDept ?? ''} onChange={(e) => setSelectedDept(e.target.value || null)}
              className="px-3 py-1.5 bg-white border border-[#E8E7E3] rounded-xl text-xs font-body text-[#6B6860] hover:border-[#C8C7BF] focus:outline-none focus:border-[#1A1916] cursor-pointer transition-colors flex-shrink-0">
              <option value="">Semua Departemen</option>
              {deptOptions.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          )}
          <button onClick={() => setCreating({})}
            className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 bg-[#1A1916] text-white rounded-xl text-sm font-medium font-body hover:bg-[#2E2E2A] active:scale-[0.98] transition-all">
            <Plus className="w-3.5 h-3.5" /> Tambah Konten
          </button>
        </div>
      </header>

      <main className="px-6 py-5 space-y-4">
        {/* Stats */}
        <div className="bg-white border border-[#E8E7E3] rounded-2xl p-5 flex items-center gap-6 flex-wrap">
          <div>
            <p className="font-display font-bold leading-none text-[#1A1916]" style={{ fontSize: '1.75rem' }}>{loading ? '—' : stats.total}</p>
            <p className="text-[9px] uppercase tracking-widest text-[#C0BDB6] font-body mt-1">Total Konten</p>
          </div>
          <div className="w-px h-10 bg-[#F0EFE9]" />
          <div className="flex items-center gap-x-5 gap-y-3 flex-wrap">
            {CONTENT_STATUS_ORDER.map((s) => {
              const c = CONTENT_STATUS_CONFIG[s]
              const val = { draft: stats.draft, planned: stats.planned, 'in-progress': stats.inprogress, published: stats.published }[s]
              const pct = stats.total > 0 ? Math.round((val / stats.total) * 100) : 0
              return (
                <div key={s} className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: c.accent }} />
                  <span className="font-display text-[17px] font-bold text-[#1A1916] leading-none tabular-nums">{loading ? '—' : val}</span>
                  <div className="leading-tight">
                    <span className="block text-[10px] uppercase tracking-wider text-[#8A8A85] font-body">{c.label}</span>
                    <span className="block text-[10px] text-[#C0BDB6] font-body">{pct}%</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Board */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-[#FAFAF8] border border-[#EEEDE8] rounded-2xl p-2.5 space-y-2.5">
                <div className="h-4 bg-[#F0EFE9] rounded w-1/2" />
                {[...Array(2)].map((_, j) => <div key={j} className="h-28 bg-white border border-[#EEEDE8] rounded-xl animate-pulse" />)}
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-12 h-12 rounded-2xl bg-white border border-[#E8E7E3] flex items-center justify-center mb-3">
              <CalendarRange className="w-5 h-5 text-[#C0BDB6]" />
            </div>
            <h3 className="font-display text-xl font-bold text-[#1A1916] mb-1.5">Belum ada konten</h3>
            <p className="text-sm text-[#8A8A85] font-body mb-5 max-w-xs">
              {search ? `Tidak ada konten cocok dengan "${search}"` : 'Mulai rencanakan konten pertamamu.'}
            </p>
            {!search && (
              <button onClick={() => setCreating({})}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#1A1916] text-white rounded-xl text-sm font-medium font-body hover:bg-[#2E2E2A]">
                <Plus className="w-4 h-4" /> Tambah Konten
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-start">
            {CONTENT_STATUS_ORDER.map((s, ci) => {
              const col = filtered.filter((p) => p.status === s)
              const c = CONTENT_STATUS_CONFIG[s]
              return (
                <div key={s}
                  onDragOver={(e) => { if (dragId) { e.preventDefault(); setOverCol(s) } }}
                  onDragLeave={() => setOverCol((c) => (c === s ? null : c))}
                  onDrop={(e) => { e.preventDefault(); dropTo(s) }}
                  className={`border rounded-2xl p-2.5 flex flex-col gap-2 min-h-[120px] transition-colors reveal reveal-d${ci + 1} ${
                    overCol === s ? 'bg-[#F3EEFF] border-[#C7B9F5]' : 'bg-[#FAFAF8] border-[#EEEDE8]'
                  }`}>
                  <div className="flex items-center gap-2 px-1 pb-1.5 border-b border-[#F0EFE9]">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${c.dot}`} />
                    <span className="text-[11px] uppercase tracking-wider font-body font-semibold text-[#6B6860]">{c.label}</span>
                    <span className="ml-auto text-[10px] font-body font-bold text-[#C0BDB6]">{col.length}</span>
                  </div>
                  {col.length === 0 ? (
                    <p className="text-[11px] text-[#C8C7BF] font-body text-center py-6">Belum ada</p>
                  ) : (
                    <div className="flex flex-col gap-2.5">
                      {col.map((plan) => (
                        <ContentCard key={plan.id} plan={plan} highlight={highlightId === plan.id}
                          dragging={dragId === plan.id}
                          onDragStart={(e) => { setDragId(plan.id); e.dataTransfer.effectAllowed = 'move' }}
                          onDragEnd={() => { setDragId(null); setOverCol(null) }}
                          onOpen={() => setEditing(plan)} onJump={() => goCalendar(plan)} />
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </main>

      {(creating || editing) && (
        <ContentPlanModal
          plan={editing}
          defaultDate={creating?.date}
          onClose={() => { setEditing(null); setCreating(null) }}
          onCreate={createPlan}
          onUpdate={updatePlan}
          onDelete={deletePlan}
        />
      )}
    </div>
  )
}
